import { DestroyRef, Directive, effect, HostListener, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/**
 * Debounce Directive
 *
 * Debounces events (input, keyup, etc.) on form controls and emits the debounced value.
 *
 * @example
 *   ```html
 *   <input
 *     appDebounce
 *     [debounceMs]="300"
 *     [appDebounceEvent]="'input'"
 *     (debouncedValue)="onSearch($event)"
 *   />
 *   ```;
 *
 * @example
 *   ```html
 *   <hx-input
 *     appDebounce
 *     [debounceMs]="500"
 *     (debouncedValue)="handleDebounce($event)"
 *   ></hx-input>
 *   ```;
 */
@Directive({
  selector: '[appDebounce]',
  standalone: true,
})
export class DebounceDirective {
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Debounce time in milliseconds
   *
   * @default 300
   */
  readonly debounceMs = input<number>(300);

  /**
   * Event type to listen for (input, keyup, change, keydown, etc.)
   *
   * @default 'input'
   */
  readonly appDebounceEvent = input<string>('input');

  /** Emits the debounced value */
  readonly debouncedValue = output<string>();

  private debounceSubject = new Subject<string>();

  constructor() {
    // Setup debounce subscription with effect to react to debounceMs changes
    effect(() => {
      this.debounceSubject
        .pipe(debounceTime(this.debounceMs()), takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          this.debouncedValue.emit(value);
        });
    });
  }

  /**
   * Listen for the specified event and emit debounced value Using HostListener dynamically would require @HostListener
   * decorator, so we need to handle this differently by checking event type
   */
  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    if (this.appDebounceEvent() === 'input') {
      this.handleEvent(event);
    }
  }

  @HostListener('keyup', ['$event'])
  onKeyUp(event: Event): void {
    if (this.appDebounceEvent() === 'keyup') {
      this.handleEvent(event);
    }
  }

  @HostListener('change', ['$event'])
  onChange(event: Event): void {
    if (this.appDebounceEvent() === 'change') {
      this.handleEvent(event);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: Event): void {
    if (this.appDebounceEvent() === 'keydown') {
      this.handleEvent(event);
    }
  }

  private handleEvent(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (target) {
      this.debounceSubject.next(target.value);
    }
  }
}
